/**
 * Test Drives — bookings with agreement lifecycle (DRAFT → SENT → SIGNED),
 * start/complete odometer + trade-plate tracking, and a Trade Plates
 * management section.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  formatInOrgTz,
  orgCivilToday, orgAddDays, orgStartOfWeek, orgRange,
  civilToISODate, isoDateToCivil, orgDaysBetween,
} from '../lib/datetime';
import TopBar from '../components/TopBar';
import FleetTabs from '../components/FleetTabs';
import LookupField from '../components/LookupField';
import { testDrivesService, leadsService, appointmentsService } from '../lib/services';
import SlotPicker, { todayISO } from '../components/SlotPicker';
import { useApi } from '../lib/hooks/useApi';
import { useToast } from '../lib/toast';
import EditConsultantControl from '../components/EditConsultantControl';
import { apiErrorMessage } from '../lib/apiError';
import {
  useConsultantOptions, useCustomerOptions, useLeadOptions, useVehicleOptions,
} from '../lib/useDirectory';
import { useDirectoryNames } from '../lib/useDirectoryNames';
import TestDriveCheckInModal from '../components/testdrive/TestDriveCheckInModal';
import CheckInRecord from '../components/testdrive/CheckInRecord';

const STATUSES = ['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
const STATUS_TONE = { PLANNED: 'info', IN_PROGRESS: 'accent', COMPLETED: 'success', CANCELLED: 'danger' };
const AGREEMENT_TONE = { DRAFT: '', SENT: 'warning', SIGNED: 'success' };
const PLATE_TONE = { AVAILABLE: 'success', IN_USE: 'warning', EXPIRED: 'danger', RETIRED: 'danger' };

function fmtWhen(iso) {
  return formatInOrgTz(iso, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * When a drive has been out long enough to be worth a manager's attention.
 * (E13 / T096.)
 *
 * A constant rather than a setting, for now. No dealership has told us 90 is
 * wrong, and a settings field nobody has asked to change is its own kind of
 * clutter — the same trap as a cadence control with no sender behind it. When
 * one of them disagrees, this becomes a lookup and the UI does not change.
 */
const LONG_DRIVE_MINUTES = 90;

/** Whole minutes the car has been out — still running when not yet returned. */
function minutesOut(d) {
  if (!d?.startedAt) return null;
  const end = d.completedAt ? new Date(d.completedAt) : new Date();
  const mins = Math.round((end - new Date(d.startedAt)) / 60000);
  return Number.isFinite(mins) ? Math.max(0, mins) : null;
}

function fmtDuration(mins) {
  if (mins == null) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Normalise a typed phone to E.164 (the backend requires a leading "+"); default
 * to Australia (+61) when no country code is given. Returns null for empty.
 */
function toE164(raw) {
  if (!raw) return null;
  let p = String(raw).replace(/[\s()\-.]/g, '');
  if (!p) return null;
  if (p.startsWith('+')) return p;
  if (p.startsWith('00')) return '+' + p.slice(2);
  if (p.startsWith('0')) p = p.slice(1);
  return '+61' + p;
}
/** A normalised phone is valid when it's "+" then 8–15 digits. */
const isValidPhone = (e164) => /^\+\d{8,15}$/.test(e164 || '');

/** km travelled, once the car is back. Null until then — not zero. */
function distanceKm(d) {
  if (d?.kmOut == null || d?.kmIn == null) return null;
  const km = d.kmIn - d.kmOut;
  return km >= 0 ? km : null;
}
function asList(d) {
  if (Array.isArray(d)) return d;
  return d?.content ?? [];
}

/* ── mocks ── */
const MOCK_TEST_DRIVES = [
  { id: 'td-1', leadId: 'lead-101', customerName: 'Harry Nguyen', vehicleId: 'veh-11', vehicleLabel: '2024 Toyota RAV4 Cruiser', consultantName: 'Mia Chen', agreementStatus: 'SIGNED', tradePlate: 'T-042', kmOut: 18450, kmIn: null, status: 'IN_PROGRESS', startedAt: new Date(Date.now() - 40 * 60000).toISOString(), completedAt: null, insuranceDeclared: true },
  { id: 'td-2', leadId: 'lead-102', customerName: 'Sofia Rossi', vehicleId: 'veh-23', vehicleLabel: '2023 Mazda CX-5 Touring', consultantName: 'Josh Patel', agreementStatus: 'SENT', tradePlate: null, kmOut: null, kmIn: null, status: 'PLANNED', scheduledAt: new Date(Date.now() + 3 * 3600e3).toISOString(), startedAt: null, completedAt: null, insuranceDeclared: true },
  { id: 'td-3', leadId: 'lead-108', customerName: 'Isla Martin', vehicleId: 'veh-30', vehicleLabel: '2025 Kia Sportage GT-Line', consultantName: 'Mia Chen', agreementStatus: 'DRAFT', tradePlate: null, kmOut: null, kmIn: null, status: 'PLANNED', scheduledAt: new Date(Date.now() + 26 * 3600e3).toISOString(), startedAt: null, completedAt: null, insuranceDeclared: false },
  { id: 'td-4', leadId: 'lead-090', customerName: 'Oliver Brown', vehicleId: 'veh-05', vehicleLabel: '2022 Hyundai Tucson N-Line', consultantName: 'Grace Kim', agreementStatus: 'SIGNED', tradePlate: 'T-017', kmOut: 33210, kmIn: 33245, status: 'COMPLETED', startedAt: new Date(Date.now() - 26 * 3600e3).toISOString(), completedAt: new Date(Date.now() - 25 * 3600e3).toISOString(), routeNotes: 'City loop', insuranceDeclared: true },
  { id: 'td-5', leadId: 'lead-115', customerName: 'Jack Wilson', vehicleId: 'veh-41', vehicleLabel: '2024 Toyota Kluger GXL', consultantName: 'Josh Patel', agreementStatus: 'DRAFT', tradePlate: null, kmOut: null, kmIn: null, status: 'CANCELLED', startedAt: null, completedAt: null, insuranceDeclared: false },
];
const MOCK_PLATES = [
  { id: 'tp-1', plateNumber: 'T-017', status: 'AVAILABLE', expiresAt: new Date(Date.now() + 200 * 86400e3).toISOString() },
  { id: 'tp-2', plateNumber: 'T-042', status: 'IN_USE', expiresAt: new Date(Date.now() + 90 * 86400e3).toISOString() },
  { id: 'tp-3', plateNumber: 'T-088', status: 'EXPIRED', expiresAt: new Date(Date.now() - 10 * 86400e3).toISOString() },
];

/* ── new test drive modal ── */
function NewTestDriveModal({ onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    leadId: '', customerId: '', vehicleId: '', consultantId: '',
    // E1 / T021: walk-in capture. A customer standing at the desk has no record
    // yet, and the old modal required an existing customerId — so the person
    // who most needs a test drive booked was the one who could not have one.
    walkIn: false, firstName: '', lastName: '', phone: '',
  });
  // A test drive booked here goes through the diary like one booked anywhere
  // else: pick a bookable slot, book the TEST_DRIVE appointment, and the
  // PLANNED drive mirrors onto this board with its time. The old direct
  // create made this module the one door that skipped the diary entirely —
  // rows with no slot, no appointment link, and a consultant nobody had
  // checked was free. A walk-in wanting the keys now just picks today's
  // next slot and checks in from the row that appears.
  const [date, setDate] = useState(todayISO());
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const consultantsQ = useConsultantOptions();
  const customersQ = useCustomerOptions();
  const vehiclesQ = useVehicleOptions({ condition: 'DEMO' });
  const customersById = useMemo(
    () => new Map(customersQ.options.map(o => [o.id, o])),
    [customersQ.options],
  );
  const leadsQ = useLeadOptions(customersById);

  // A lead already knows its customer — keep the two from disagreeing.
  const onPickLead = (leadId, lead) => {
    setForm((f) => ({
      ...f,
      leadId: leadId || '',
      customerId: lead?.customerId || (leadId ? f.customerId : ''),
      // Picking a lead is the "existing" path — switch out of walk-in and drop
      // any half-entered walk-in details so only one customer source is active.
      ...(leadId ? { walkIn: false, firstName: '', lastName: '', phone: '' } : {}),
    }));
  };
  const leadPinsCustomer = !!(
    form.leadId && leadsQ.options.find(l => l.id === form.leadId)?.customerId
  );

  // The mirror needs a customer on the drive (test_drives.customer_id is NOT
  // NULL), and the appointment needs a slot; the consultant comes with the
  // slot, and the vehicle is the point of booking from the stock board.
  // Walk-in phone: normalise to E.164 and require a valid number — the backend
  // rejects anything that isn't E.164, and a blank/garbage phone used to 500.
  const walkInPhoneE164 = form.walkIn ? toE164(form.phone) : null;
  const walkInPhoneValid = !form.walkIn || isValidPhone(walkInPhoneE164);
  // Names: letters (any language), spaces, hyphens, apostrophes, periods —
  // no digits. First name required; last name optional but valid when present.
  const NAME_RE = /^[\p{L}][\p{L}\s'.-]{0,49}$/u;
  const firstNameValid = NAME_RE.test(form.firstName.trim());
  const lastNameValid = !form.lastName.trim() || NAME_RE.test(form.lastName.trim());
  const canSubmit = !busy && form.vehicleId && slot && (
    form.walkIn ? (firstNameValid && lastNameValid && walkInPhoneValid) : !!form.customerId
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      let leadId = form.leadId || null;
      let customerId = form.customerId;

      if (form.walkIn) {
        // One action, three records. The lead endpoint resolves-or-creates the
        // customer, so creating the lead is what creates the contact — there is
        // no separate customer call to get out of step with it.
        const lead = await leadsService.create({
          source: 'WALK_IN',
          customerFirstName: form.firstName.trim() || null,
          customerLastName: form.lastName.trim() || null,
          customerPhoneE164: walkInPhoneE164,
          vehicleOfInterestId: form.vehicleId || null,
        });
        leadId = lead?.id || null;
        customerId = lead?.customerId || null;
        if (!customerId) throw new Error('The lead was created without a customer — cannot book the drive against it.');
        // Best-effort: the drive is the point, the stage is bookkeeping.
        try {
          await leadsService.updateStage(leadId, 'TEST_DRIVE', { reason: 'Walk-in test drive booked at the desk' });
        } catch { /* the drive below still books; the lead simply stays at Inbound */ }
      }

      // Book the TEST_DRIVE appointment; TestDriveBookingSync mirrors the
      // PLANNED drive onto this board before the response returns.
      await appointmentsService.create({
        type: 'TEST_DRIVE',
        startsAt: slot.startsAt,
        endsAt: slot.endsAt,
        consultantId: form.consultantId || slot.consultantId,
        leadId,
        customerId,
        vehicleId: form.vehicleId,
        source: 'HUMAN', // → backend default status CONFIRMED
      });
      toast(form.walkIn ? 'Walk-in captured and test drive booked' : 'Test drive booked', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      toast(`Create failed: ${apiErrorMessage(err)}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(520px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">New test drive</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <LookupField
            id="td-vehicle"
            label="Vehicle"
            required
            placeholder="Search by model, stock # or VIN…"
            emptyHint="No vehicle matches that search"
            options={vehiclesQ.options}
            loading={vehiclesQ.loading}
            value={form.vehicleId || null}
            onChange={(id) => set('vehicleId', id || '')}
          />

          <LookupField
            id="td-consultant"
            label="Consultant (optional — narrows the slots)"
            placeholder="Search by name, email or role…"
            emptyHint="No consultant matches that search"
            options={consultantsQ.options}
            loading={consultantsQ.loading}
            error={consultantsQ.error}
            value={form.consultantId || null}
            onChange={(id) => set('consultantId', id || '')}
          />

          <SlotPicker
            date={date}
            onDateChange={setDate}
            type="TEST_DRIVE"
            consultantId={form.consultantId || undefined}
            value={slot}
            onSelect={setSlot}
            disabled={busy}
          />

          <LookupField
            id="td-lead"
            label="Lead"
            placeholder={form.walkIn ? 'Disabled — entering a new walk-in' : 'Search by customer, vehicle or stage…'}
            emptyHint="No lead matches that search"
            options={leadsQ.options}
            loading={leadsQ.loading}
            value={form.leadId || null}
            onChange={onPickLead}
            disabled={form.walkIn || busy}
          />

          {/* Existing record, or someone standing at the desk (T021). */}
          <div className="flex-row" style={{ gap: 4 }} role="group" aria-label="Customer type">
            <button
              type="button"
              className={`pill${!form.walkIn ? ' active' : ''}`}
              aria-pressed={!form.walkIn}
              onClick={() => set('walkIn', false)}
            >Existing customer</button>
            <button
              type="button"
              className={`pill${form.walkIn ? ' active' : ''}`}
              aria-pressed={form.walkIn}
              onClick={() => setForm((f) => ({ ...f, walkIn: true, customerId: '', leadId: '' }))}
              title="Creates the contact, the lead and the drive in one action"
            >New walk-in</button>
          </div>

          {form.walkIn ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-field">
                  <label htmlFor="td-first">First name</label>
                  <input id="td-first" value={form.firstName} maxLength={50} autoFocus
                    onChange={(e) => set('firstName', e.target.value.replace(/[^\p{L}\s'.-]/gu, ''))} />
                  {form.firstName.trim() && !firstNameValid && (
                    <div className="text-xs" style={{ color: 'var(--danger, #c53030)', marginTop: 4 }}>Enter a valid name.</div>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="td-last">Last name</label>
                  <input id="td-last" value={form.lastName} maxLength={50}
                    onChange={(e) => set('lastName', e.target.value.replace(/[^\p{L}\s'.-]/gu, ''))} />
                  {form.lastName.trim() && !lastNameValid && (
                    <div className="text-xs" style={{ color: 'var(--danger, #c53030)', marginTop: 4 }}>Enter a valid name.</div>
                  )}
                </div>
              </div>
              <div className="form-field">
                <label htmlFor="td-phone">Phone</label>
                <input id="td-phone" type="tel" inputMode="tel" value={form.phone}
                  onChange={(e) => set('phone', e.target.value.replace(/[^\d+\s()\-]/g, ''))}
                  placeholder="0400 000 000 or +61…" />
                {form.phone.trim() && !walkInPhoneValid && (
                  <div className="text-xs" style={{ color: 'var(--danger, #c53030)', marginTop: 4 }}>
                    Enter a valid phone number, e.g. 0400 000 000 or +61 400 000 000.
                  </div>
                )}
              </div>
              <div className="mono text-xs text-muted" style={{ marginTop: -4 }}>
                Creates the customer, a lead at Test Drive and this booking together.
                Name and phone are all that is needed — the rest can be filled in after they drive.
              </div>
            </>
          ) : (
            <>
              <LookupField
                id="td-customer"
                label="Customer"
                required
                placeholder="Search by name, phone or email…"
                emptyHint="No customer matches that search"
                options={customersQ.options}
                loading={customersQ.loading}
                value={form.customerId || null}
                onChange={(id) => set('customerId', id || '')}
                disabled={leadPinsCustomer}
              />
              {leadPinsCustomer && (
                <div className="mono text-xs text-muted" style={{ marginTop: -6 }}>
                  Customer is taken from the selected lead. Clear the lead to choose a different one.
                </div>
              )}
            </>
          )}

          <div className="mono text-xs text-muted">
            Licence photo, signed agreement and insurance declaration are captured
            at check-in, when the customer arrives to drive.
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Booking…' : 'Book test drive'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── add trade plate modal ── */
function NewPlateModal({ open, onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ plateNumber: '', expiresAt: '' });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  // Native date inputs accept 5–6 digit years (275760). Validate numerically:
  // a plate expiry must be a 4-digit year within the next 20 years and not in
  // the past (an already-expired plate shouldn't be added).
  const curYear = new Date().getFullYear();
  const todayStr = new Date().toLocaleDateString('en-CA');   // YYYY-MM-DD, local
  const maxExpiryStr = `${curYear + 20}-12-31`;
  const expYear = form.expiresAt ? Number(String(form.expiresAt).split('-')[0]) : null;
  const expiryBad = !!form.expiresAt && (
    !Number.isInteger(expYear) || expYear < curYear || expYear > curYear + 20
    || form.expiresAt < todayStr
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.plateNumber.trim() || expiryBad) return;
    setBusy(true);
    try {
      await testDrivesService.createTradePlate({
        plateNumber: form.plateNumber.trim().toUpperCase(),
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
      });
      toast('Trade plate added', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      toast(`Add failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(420px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">Add trade plate</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-field">
            <label>Plate number *</label>
            <input value={form.plateNumber} onChange={(e) => setForm((f) => ({ ...f, plateNumber: e.target.value }))} placeholder="T-099" autoFocus required />
          </div>
          <div className="form-field">
            <label>Expiry</label>
            <input type="date" value={form.expiresAt} min={todayStr} max={maxExpiryStr}
              onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))} />
            {expiryBad && (
              <div className="text-xs" style={{ color: 'var(--danger, #c53030)', marginTop: 4 }}>
                Enter a valid future expiry — a 4-digit year within the next 20 years.
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy || !form.plateNumber.trim() || expiryBad}>
            {busy ? 'Adding…' : 'Add plate'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── drawer ── */
function TestDriveDrawer({ drive, onClose, onChanged }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [checkin, setCheckin] = useState(false);
  const [kmIn, setKmIn] = useState('');
  const [routeNotes, setRouteNotes] = useState('');

  if (!drive) return null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast(okMsg, { variant: 'success' });
      onChanged();
      onClose();
    } catch (e) {
      const body = e?.response?.data;
      const code = body?.errorCode || body?.code || body?.error || '';
      if (String(code).includes('TEST_DRIVE_NOT_READY') || String(body?.message || '').includes('TEST_DRIVE_NOT_READY')) {
        toast('Not ready to start — licence, signed agreement and insurance declaration are all required', { variant: 'warning' });
      } else {
        toast(`Action failed: ${body?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
      }
    } finally {
      setBusy(false);
    }
  };

  const complete = () => run(
    () => testDrivesService.complete(drive.id, { kmIn: Number(kmIn) || 0, routeNotes: routeNotes.trim() || null }),
    'Test drive completed',
  );
  const cancel = () => run(
    () => testDrivesService.update(drive.id, { status: 'CANCELLED' }),
    'Test drive cancelled',
  );

  // Odometer sanity (matches the backend cap, DEF-98): KM in can't be below
  // KM out, and the distance can't exceed a plausible test-drive length.
  const MAX_DISTANCE_KM = 20;
  const kmInNum = Number(kmIn);
  const distance = drive.kmOut != null && kmIn !== '' ? kmInNum - drive.kmOut : null;
  const kmBelowOut = drive.kmOut != null && kmIn !== '' && kmInNum < drive.kmOut;
  const kmTooFar = distance != null && distance > MAX_DISTANCE_KM;

  const row = (label, value) => (
    <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
      <span className="text-muted" style={{ width: 110, flexShrink: 0 }}>{label}</span>
      <span>{value ?? '—'}</span>
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200 }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(400px, 92vw)',
        background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-lg)', zIndex: 201, padding: 20,
        display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{drive._vehicle || drive.vehicleLabel || 'Test drive'}</div>
            <div className="mono text-xs text-muted">{drive._customer || '—'}</div>
          </div>
          <button className="btn small" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <span className={`pill filled ${STATUS_TONE[drive.status] || ''}`}>{(drive.status || '').replace('_', ' ')}</span>
          <span className={`pill ${AGREEMENT_TONE[drive.agreementStatus] || ''}`}>Agreement: {drive.agreementStatus || 'DRAFT'}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {row('Customer', drive._customer)}
          {drive._vehicle && row('Vehicle', drive._vehicle)}
          {drive._lead && row('Lead', drive._lead)}
          {row('Consultant', drive._consultant)}
          {/* Only a drive that hasn't started and is backed by an appointment:
              the consultant lives on the appointment, and a started drive keeps
              the person who is running it. */}
          {drive.status === 'PLANNED' && drive.appointmentId && (
            <div style={{ marginLeft: 120 }}>
              <EditConsultantControl
                appointmentId={drive.appointmentId}
                consultantId={drive.consultantId}
                onChanged={() => { onChanged(); onClose(); }}
              />
            </div>
          )}
          {row('Trade plate', drive.tradePlate || (drive.tradePlateId ? '—' : null))}
          {row('KM out', drive.kmOut != null ? drive.kmOut.toLocaleString('en-AU') : '—')}
          {row('KM in', drive.kmIn != null ? drive.kmIn.toLocaleString('en-AU') : '—')}
          {row('Scheduled', fmtWhen(drive.scheduledAt))}
          {row('Started', fmtWhen(drive.startedAt))}
          {row('Completed', fmtWhen(drive.completedAt))}
          {row('Insurance', drive.insuranceDeclared ? 'Declared' : 'Not declared')}
          {drive.routeNotes && row('Route', drive.routeNotes)}
        </div>

        {drive.status !== 'PLANNED' && !String(drive.id).startsWith('td-') && <CheckInRecord drive={drive} />}

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {drive.status === 'PLANNED' && (
            <>
              <button className="btn small primary" disabled={busy} onClick={() => setCheckin(true)}>
                🔑 Start test drive
              </button>
              <div className="mono text-xs text-muted" style={{ marginTop: -6 }}>
                Licence photo · documents · customer signature · keys
              </div>
              {checkin && (
                <TestDriveCheckInModal
                  drive={drive}
                  customerName={drive._customer}
                  vehicleLabel={drive._vehicle || drive.vehicleLabel}
                  onClose={() => setCheckin(false)}
                  onDone={() => { onChanged(); onClose(); }}
                />
              )}
            </>
          )}

          {drive.status === 'IN_PROGRESS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="form-field">
                <label>KM in {drive.kmOut != null && <span className="text-muted">· out at {drive.kmOut.toLocaleString('en-AU')}</span>}</label>
                <input type="number" min={drive.kmOut ?? 0} value={kmIn} onChange={(e) => setKmIn(e.target.value)} placeholder="18478" />
                {kmBelowOut && (
                  <div className="text-xs" style={{ color: 'var(--danger)', marginTop: 4 }}>
                    KM in can’t be less than KM out ({drive.kmOut.toLocaleString('en-AU')}).
                  </div>
                )}
                {kmTooFar && (
                  <div className="text-xs" style={{ color: 'var(--danger)', marginTop: 4 }}>
                    That’s a {distance.toLocaleString('en-AU')} km drive — check the reading
                    (a test drive is normally under {MAX_DISTANCE_KM} km).
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>Route notes</label>
                <textarea rows={2} value={routeNotes} onChange={(e) => setRouteNotes(e.target.value)} placeholder="Highway + city loop" />
              </div>
              <button className="btn small primary" disabled={busy || kmIn === '' || kmBelowOut || kmTooFar} onClick={complete}>Complete drive</button>
            </div>
          )}

          {['PLANNED', 'IN_PROGRESS'].includes(drive.status) && (
            <button className="btn small danger" disabled={busy} onClick={cancel}>Cancel test drive</button>
          )}
          {['COMPLETED', 'CANCELLED'].includes(drive.status) && (
            <div className="text-muted" style={{ fontSize: 12 }}>This test drive is closed — no further actions.</div>
          )}
        </div>
      </aside>
    </>
  );
}

/* ── page ── */
const VIEWS = [['DAY', 'Day'], ['WEEK', 'Week'], ['RANGE', 'Range'], ['ALL', 'All']];
const MAX_RANGE_DAYS = 62;
const PAGE_SIZE = 200;

export default function TestDrives() {
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [tdModalOpen, setTdModalOpen] = useState(false);
  const [plateModalOpen, setPlateModalOpen] = useState(false);

  /* ── which days are we showing (E13 / T094) ────────────────────────────
     The window is sent to the server, and the server measures it against the
     day the car went out — not the day the record was typed, which is what it
     used to do and which filed a Friday booking taken on Monday under Friday
     (D37). ALL is kept because a register is small and "everything" is a
     question a dealer principal actually asks; the diary has no equivalent
     because a calendar with no dates is meaningless. */
  const [view, setView] = useState('WEEK');
  const [anchor, setAnchor] = useState(() => orgCivilToday());
  const [rangeFrom, setRangeFrom] = useState(() => orgStartOfWeek(orgCivilToday()));
  const [rangeTo, setRangeTo] = useState(() => orgAddDays(orgStartOfWeek(orgCivilToday()), 6));

  const days = useMemo(() => {
    if (view === 'DAY') return [anchor];
    if (view === 'RANGE') {
      const n = Math.min(MAX_RANGE_DAYS, Math.max(1, orgDaysBetween(rangeFrom, rangeTo) + 1));
      return Array.from({ length: n }, (_, i) => orgAddDays(rangeFrom, i));
    }
    const ws = orgStartOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => orgAddDays(ws, i));
  }, [view, anchor, rangeFrom, rangeTo]);

  const window_ = useMemo(
    () => (view === 'ALL' ? { fromISO: undefined, toISO: undefined }
      : orgRange(days[0], days[days.length - 1])),
    [view, days],
  );

  const step = view === 'DAY' ? 1 : (view === 'RANGE' ? days.length : 7);
  const go = (dir) => {
    if (view === 'RANGE') {
      setRangeFrom((f) => orgAddDays(f, dir * step));
      setRangeTo((t) => orgAddDays(t, dir * step));
    } else {
      setAnchor((a) => orgAddDays(a, dir * step));
    }
  };
  const goToday = () => {
    const t = orgCivilToday();
    setAnchor(t);
    setRangeFrom(orgStartOfWeek(t));
    setRangeTo(orgAddDays(orgStartOfWeek(t), 6));
  };

  // size, not limit. The controller's parameter is `size`; `limit` was being
  // dropped silently, so the register has been capped at the default 20 rows
  // and every status count above that was wrong (D41).
  const drivesQ = useApi(
    () => testDrivesService.list({ page: 0, size: PAGE_SIZE, from: window_.fromISO, to: window_.toISO }),
    { devFallback: MOCK_TEST_DRIVES, deps: [window_.fromISO, window_.toISO] },
  );
  const platesQ = useApi(() => testDrivesService.tradePlates(), { devFallback: MOCK_PLATES });

  const names = useDirectoryNames();
  const plates = asList(platesQ.data);

  // Resolve customer / vehicle / consultant / lead names so the table and
  // drawer show people and cars, not UUIDs.
  const drives = useMemo(() => asList(drivesQ.data).map((d) => ({
    ...d,
    _customer: names.customer(d.customerId, d.customerName, { dash: null }),
    _vehicle: names.vehicle(d.vehicleId, d.vehicleLabel, { dash: null }),
    _consultant: names.consultant(d.consultantId, d.consultantName, { dash: null }),
    _lead: names.lead(d.leadId, d.leadName, { dash: null }),
  })), [drivesQ.data, names]);

  const filtered = useMemo(
    () => drives.filter((d) => !statusFilter || d.status === statusFilter),
    [drives, statusFilter],
  );
  const countFor = (s) => drives.filter((d) => d.status === s).length;

  // The status chips filter inside the window that was fetched, so the counts
  // are only true if the whole window arrived. Say so when it did not, rather
  // than showing a confident number that is quietly a page of it.
  const totalInWindow = drivesQ.data?.totalElements;
  const truncated = totalInWindow != null && totalInWindow > drives.length;

  /* ── what is still out (E13 / T096) ─────────────────────────────────────
     The reason this screen exists. A car that left at 10am and is still out at
     4pm is the single most useful thing it can tell a sales manager, and it is
     no use at all if you have to filter for it. */
  const open = useMemo(() => drives.filter((d) => d.status === 'IN_PROGRESS'), [drives]);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!open.length) return undefined;          // no clock when nothing is out
    const t = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, [open.length]);
  const overdue = open.filter((d) => (minutesOut(d) ?? 0) > LONG_DRIVE_MINUTES);

  return (
    <>
      <TopBar
        crumbs={['Vehicles out', 'Test drives']}
        right={
          <>
            <button className="btn small" onClick={drivesQ.refetch}>↻ Refresh</button>
            <button className="btn small primary" onClick={() => setTdModalOpen(true)}>+ New test drive</button>
          </>
        }
      />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FleetTabs />

        {/* ── Day / week / range navigator (T094) ── */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {VIEWS.map(([k, label]) => (
              <button key={k} className={`btn small ${view === k ? 'primary' : ''}`}
                onClick={() => setView(k)}>{label}</button>
            ))}
          </div>
          {view !== 'ALL' && (
            <>
              <button className="btn small" onClick={() => go(-1)} aria-label="Previous">‹</button>
              <button className="btn small" onClick={goToday}>Today</button>
              <button className="btn small" onClick={() => go(1)} aria-label="Next">›</button>
            </>
          )}
          {view === 'RANGE' && (
            <>
              <input type="date" value={civilToISODate(rangeFrom)}
                onChange={(e) => setRangeFrom(isoDateToCivil(e.target.value))} />
              <span className="text-muted">to</span>
              <input type="date" value={civilToISODate(rangeTo)}
                onChange={(e) => setRangeTo(isoDateToCivil(e.target.value))} />
            </>
          )}
          {view !== 'ALL' && (
            <span className="mono text-xs text-muted">
              {formatInOrgTz(window_.fromISO, { day: 'numeric', month: 'short' })}
              {/* The last DAY, not the window's end instant. toISO is midnight
                  at the close of the last day, which in the org zone reads as
                  the next date — a week of 7–13 Sept was labelled "7 – 14". */}
              {days.length > 1 && ` – ${formatInOrgTz(
                orgRange(days[days.length - 1], days[days.length - 1]).fromISO,
                { day: 'numeric', month: 'short' },
              )}`}
              {' · by the day the car went out'}
            </span>
          )}
        </div>

        {/* ── What is still out (T096) ── */}
        {open.length > 0 && (
          <div
            role="status"
            className="mono text-xs"
            style={{
              padding: '8px 10px', borderRadius: 6,
              display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
              background: overdue.length
                ? 'color-mix(in srgb, var(--warning) 12%, transparent)'
                : 'color-mix(in srgb, var(--accent) 10%, transparent)',
              color: overdue.length ? 'var(--warning)' : 'var(--accent)',
            }}
          >
            <span><b>{open.length}</b> {open.length === 1 ? 'car is' : 'cars are'} still out</span>
            {overdue.length > 0 && (
              <span><b>{overdue.length}</b> out longer than {fmtDuration(LONG_DRIVE_MINUTES)}</span>
            )}
            <span style={{ opacity: 0.75 }}>
              longest {fmtDuration(Math.max(...open.map((d) => minutesOut(d) ?? 0)))}
            </span>
          </div>
        )}

        {/* Status chips */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            className={`pill ${statusFilter === '' ? 'active' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter('')}
          >All ({drives.length})</span>
          {STATUSES.map((s) => (
            <span
              key={s}
              className={`pill ${statusFilter === s ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter(s)}
            >{s.replace('_', ' ')} ({countFor(s)})</span>
          ))}
          {drivesQ.loading && <span className="mono text-xs text-muted">loading…</span>}
          {truncated && (
            <span className="mono text-xs" style={{ color: 'var(--warning)' }}>
              showing {drives.length} of {totalInWindow} — narrow the window; the counts above are for what is shown
            </span>
          )}
        </div>

        {/* Test drive table */}
        <div className="card" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div className="empty-state" style={{ padding: 40 }}>
              <div className="icon">🚙</div>
              <div className="title">No test drives</div>
              <div className="desc">Create one from a lead or with the button above.</div>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer / Lead</th><th>Vehicle</th><th>Consultant</th><th>Agreement</th>
                    <th>Plate</th><th>KM out / in</th><th>Distance</th><th>Duration</th>
                    <th>Status</th><th>Scheduled</th><th>Started</th><th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.id} onClick={() => setSelected(d)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{d._customer || '—'}</div>
                        {d._lead && <div className="mono text-xs text-muted">Lead: {d._lead}</div>}
                      </td>
                      <td>{d._vehicle || '—'}</td>
                      <td>{d._consultant || '—'}</td>
                      <td><span className={`pill filled ${AGREEMENT_TONE[d.agreementStatus] || ''}`}>{d.agreementStatus || 'DRAFT'}</span></td>
                      <td className="mono text-xs">{d.tradePlate || d.tradePlateId || '—'}</td>
                      <td className="mono text-xs">
                        {d.kmOut != null ? d.kmOut.toLocaleString('en-AU') : '—'} / {d.kmIn != null ? d.kmIn.toLocaleString('en-AU') : '—'}
                      </td>
                      <td className="mono text-xs">
                        {distanceKm(d) != null ? `${distanceKm(d).toLocaleString('en-AU')} km` : '—'}
                      </td>
                      <td className="mono text-xs">{fmtDuration(minutesOut(d))}</td>
                      <td>
                        {d.status === 'IN_PROGRESS' ? (
                          // Carries the word, not just a colour — the same rule
                          // ownership follows on the lead board (E5 / T041), and
                          // the reason it survives the three themes.
                          <span
                            className="pill filled"
                            title={`Out since ${fmtWhen(d.startedAt)}`}
                            style={(minutesOut(d) ?? 0) > LONG_DRIVE_MINUTES
                              ? { background: 'color-mix(in srgb, var(--warning) 16%, transparent)', color: 'var(--warning)' }
                              : { background: 'color-mix(in srgb, var(--accent) 14%, transparent)', color: 'var(--accent)' }}
                          >
                            Out {fmtDuration(minutesOut(d))}
                          </span>
                        ) : (
                          <span className={`pill ${STATUS_TONE[d.status] || ''}`}>{(d.status || '—').replace('_', ' ')}</span>
                        )}
                      </td>
                      <td>{fmtWhen(d.scheduledAt)}</td>
                      <td>{fmtWhen(d.startedAt)}</td>
                      <td>{fmtWhen(d.completedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Trade plates */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Trade plates</div>
              <div className="mono text-xs text-muted">Dealer plates fitted to unregistered stock during drives</div>
            </div>
            <span style={{ marginLeft: 'auto' }} />
            {platesQ.loading && <span className="mono text-xs text-muted">loading…</span>}
            <button className="btn small primary" onClick={() => setPlateModalOpen(true)}>+ Add plate</button>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>Plate</th><th>Status</th><th>Expiry</th></tr>
              </thead>
              <tbody>
                {plates.map((p) => (
                  <tr key={p.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{p.plateNumber}</td>
                    <td><span className={`pill filled ${PLATE_TONE[p.status] || ''}`}>{(p.status || '—').replace('_', ' ')}</span></td>
                    <td>{formatInOrgTz(p.expiresAt, { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <TestDriveDrawer drive={selected} onClose={() => setSelected(null)} onChanged={drivesQ.refetch} />
      {/* Mounted only while open — its lookups fetch on mount. */}
      {tdModalOpen && (
        <NewTestDriveModal onClose={() => setTdModalOpen(false)} onCreated={drivesQ.refetch} />
      )}
      <NewPlateModal open={plateModalOpen} onClose={() => setPlateModalOpen(false)} onCreated={platesQ.refetch} />
    </>
  );
}
